/**
 * Maps shared fields across different forms
 * When a field is updated in one form, it automatically updates corresponding fields in other forms
 */

// Define field groups that should be synced across forms
export const SHARED_FIELD_GROUPS = {
  // Student first name variations
  // NOTE: student_name is NOT included here because it's a full name field and should not sync back to first/last name fields
  student_first_name: [
    'legal_name_first',
    'student_first_name'
  ],
  
  // Student last name variations
  student_last_name: [
    'legal_name_last',
    'student_last_name'
  ],
  
  // Student date of birth variations
  student_date_of_birth: [
    'date_of_birth',
    'student_birth_date'
  ],
  
  // Student grade variations
  student_grade: [
    'grade'
  ],
  
  // Student gender/sex variations
  student_gender: [
    'gender',
    'sex'
  ]
};

/**
 * Extracts the canonical field name from a prefixed field name
 * @param {string} prefixedFieldName - Field name like "uuid_student_first_name"
 * @returns {object} - { instanceId, originalFieldName }
 */
export const parsePrefixedFieldName = (prefixedFieldName) => {
  const uuidPattern = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_(.+)$/i;
  const match = prefixedFieldName.match(uuidPattern);
  
  if (match) {
    return {
      instanceId: match[1],
      originalFieldName: match[2]
    };
  }
  
  return null;
};

/**
 * Finds which shared field group a field belongs to
 * @param {string} originalFieldName - The original field name (without prefix)
 * @returns {string|null} - The shared field group key, or null if not shared
 */
export const getSharedFieldGroup = (originalFieldName) => {
  for (const [groupKey, fieldNames] of Object.entries(SHARED_FIELD_GROUPS)) {
    if (fieldNames.includes(originalFieldName)) {
      return groupKey;
    }
  }
  return null;
};

/**
 * Gets all field names in a shared field group
 * @param {string} groupKey - The shared field group key
 * @returns {array} - Array of field names in the group
 */
export const getFieldsInGroup = (groupKey) => {
  return SHARED_FIELD_GROUPS[groupKey] || [];
};

/**
 * Checks if a field exists in a form instance
 * @param {object} instance - Form instance object
 * @param {string} fieldName - Original field name (without prefix)
 * @returns {boolean} - True if field exists in the instance
 */
const fieldExistsInInstance = (instance, fieldName) => {
  if (!instance || !instance.form_data) return false;
  
  let formData = instance.form_data;
  
  // Parse if string
  if (typeof formData === 'string') {
    try {
      formData = JSON.parse(formData);
    } catch (e) {
      return false;
    }
  }
  
  if (!formData.sections || !Array.isArray(formData.sections)) return false;
  
  // Search through all sections and fields
  for (const section of formData.sections) {
    if (section.fields && Array.isArray(section.fields)) {
      for (const field of section.fields) {
        if (field.name === fieldName && !field.admin_only) {
          return true;
        }
      }
    }
  }
  
  return false;
};

/**
 * Finds all fields that should be synced when a field changes
 * @param {string} changedFieldName - The prefixed field name that changed
 * @param {object} formInstanceMap - Map of prefixed field names to instance IDs
 * @param {array} formInstances - Array of form instances
 * @returns {array} - Array of prefixed field names that should be synced
 */
export const getFieldsToSync = (changedFieldName, formInstanceMap, formInstances) => {
  const parsed = parsePrefixedFieldName(changedFieldName);
  if (!parsed) return [];
  
  const { instanceId: sourceInstanceId, originalFieldName } = parsed;
  const sharedGroup = getSharedFieldGroup(originalFieldName);
  
  if (!sharedGroup) return [];
  
  const fieldsToSync = [];
  const relatedFieldNames = getFieldsInGroup(sharedGroup);
  
  // Find all instances that have fields in this shared group
  formInstances.forEach(instance => {
    const instanceId = instance.id;
    
    // Skip the source instance (don't sync to itself)
    if (instanceId === sourceInstanceId) return;
    
    // Check each related field name
    relatedFieldNames.forEach(relatedFieldName => {
      // Check if this field exists in the form instance
      if (fieldExistsInInstance(instance, relatedFieldName)) {
        const prefixedName = `${instanceId}_${relatedFieldName}`;
        fieldsToSync.push({
          prefixedFieldName: prefixedName,
          originalFieldName: relatedFieldName,
          instanceId: instanceId
        });
      }
    });
  });
  
  return fieldsToSync;
};

/**
 * Converts a value to match the target field type
 * Handles special cases like combining first/last name into full name
 * @param {any} value - The value to convert
 * @param {string} sourceFieldName - The source field name (prefixed)
 * @param {string} targetFieldName - The target field name (original, not prefixed)
 * @param {object} currentFormValues - Current form values (for context)
 * @param {string} sourceOriginalFieldName - The original field name from source (without prefix)
 * @returns {any} - The converted value
 */
export const convertFieldValue = (value, sourceFieldName, targetFieldName, currentFormValues = {}, sourceOriginalFieldName = null) => {
  // Special handling for student_name (full name) field
  if (targetFieldName === 'student_name') {
    const sourceParsed = parsePrefixedFieldName(sourceFieldName);
    const sourceInstanceId = sourceParsed?.instanceId;
    const sourceOriginal = sourceOriginalFieldName || sourceParsed?.originalFieldName;
    
    // Look for first name in the same instance
    let firstName = '';
    let lastName = '';
    
    if (sourceOriginal === 'legal_name_first' || sourceOriginal === 'student_first_name') {
      firstName = value || '';
      // Find last name in same instance
      Object.keys(currentFormValues).forEach(key => {
        const parsed = parsePrefixedFieldName(key);
        if (parsed?.instanceId === sourceInstanceId) {
          if (parsed.originalFieldName === 'legal_name_last' || parsed.originalFieldName === 'student_last_name') {
            lastName = currentFormValues[key] || '';
          }
        }
      });
      return `${firstName} ${lastName}`.trim();
    }
    
    if (sourceOriginal === 'legal_name_last' || sourceOriginal === 'student_last_name') {
      lastName = value || '';
      // Find first name in same instance
      Object.keys(currentFormValues).forEach(key => {
        const parsed = parsePrefixedFieldName(key);
        if (parsed?.instanceId === sourceInstanceId) {
          if (parsed.originalFieldName === 'legal_name_first' || parsed.originalFieldName === 'student_first_name') {
            firstName = currentFormValues[key] || '';
          }
        }
      });
      return `${firstName} ${lastName}`.trim();
    }
    
    // Fallback: return the value as-is
    return value;
  }
  
  // Special handling for grade field value conversion
  if (targetFieldName === 'grade') {
    // Convert between different grade formats
    if (value === '1st') return '1';
    if (value === '2nd') return '2';
    if (value === '3rd') return '3';
    if (value === '4th') return '4';
    if (value === '5th') return '5';
    if (value === '6th') return '6';
    if (value === '7th') return '7';
    if (value === '8th') return '8';
    if (value === '9th') return '9';
    if (value === '10th') return '10';
    if (value === '11th') return '11';
    if (value === '12th') return '12';
    // Handle reverse conversion
    if (value === '1') return '1st';
    if (value === '2') return '2nd';
    if (value === '3') return '3rd';
    if (value === '4') return '4th';
    if (value === '5') return '5th';
    if (value === '6') return '6th';
    if (value === '7') return '7th';
    if (value === '8') return '8th';
    if (value === '9') return '9th';
    if (value === '10') return '10th';
    if (value === '11') return '11th';
    if (value === '12') return '12th';
  }

  // For all other fields, return value as-is
  return value;
};

/**
 * Syncs a field value to related fields across forms
 * @param {string} changedFieldName - The prefixed field name that changed
 * @param {any} newValue - The new value
 * @param {object} currentFormValues - Current form values
 * @param {object} formInstanceMap - Map of prefixed field names to instance IDs
 * @param {array} formInstances - Array of form instances
 * @returns {object} - Object with field names as keys and values to set
 */
export const syncSharedFields = (changedFieldName, newValue, currentFormValues, formInstanceMap, formInstances) => {
  const fieldsToSync = getFieldsToSync(changedFieldName, formInstanceMap, formInstances);
  const updates = {};
  
  const sourceParsed = parsePrefixedFieldName(changedFieldName);
  const sourceOriginalFieldName = sourceParsed?.originalFieldName;
  
  fieldsToSync.forEach(({ prefixedFieldName, originalFieldName }) => {
    const convertedValue = convertFieldValue(
      newValue,
      changedFieldName,
      originalFieldName,
      currentFormValues,
      sourceOriginalFieldName
    );
    
    updates[prefixedFieldName] = convertedValue;
  });
  
  return updates;
};

